import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { courseService, enrollmentService } from '../../api';
import { useAuth } from '../../context/AuthContext';
import LessonPlayer from '../../components/Lesson/Player';
import toast from 'react-hot-toast';
import {
    ArrowLeft,
    Award,
    BookOpen,
    CheckCircle,
    ChevronDown,
    ChevronRight,
    Clock3,
    GraduationCap,
    Layers,
    Lock,
    PlayCircle,
    Sparkles,
    Timer,
} from 'lucide-react';
import {
    canAccessCourseContent,
    findCourseEnrollment,
    isEnrollmentCompleted,
} from '../../utils/enrollmentHelpers';

function parseList(res) {
    const data = res?.data ?? res;
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data)) return data;
    return [];
}


function CourseDetail() {
    const { id } = useParams();
    const { isAuthenticated } = useAuth();
    const [course, setCourse] = useState(null);
    const [subjects, setSubjects] = useState([]);
    const [enrollment, setEnrollment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeLesson, setActiveLesson] = useState(null);
    const [expandedSubjects, setExpandedSubjects] = useState({});

    const isEnrolled = canAccessCourseContent(enrollment);
    const isPendingApproval = enrollment?.status === 'pending';
    const isCompleted = isEnrollmentCompleted(enrollment);
    const canAccessLessons = canAccessCourseContent(enrollment);

    const lessonStats = useMemo(() => {
        const lessons = subjects.flatMap((s) => s.lessons || []);
        const totalMinutes = lessons.reduce((sum, l) => sum + (l.duration_minutes || 0), 0);
        return { count: lessons.length, totalMinutes };
    }, [subjects]);

    const progressPct = Math.round(Number(enrollment?.progress_percentage) || 0);

    const findFirstLesson = useCallback((subjectList) => {
        for (const subj of subjectList) {
            if (subj.lessons?.length) return subj.lessons[0];
        }
        return null;
    }, []);

    useEffect(() => {
        let cancelled = false;

        const fetchCourseData = async () => {
            setLoading(true);
            setActiveLesson(null);

            try {
                const requests = [
                    courseService.getCourseDetail(id),
                    courseService.getSubjects({ course: id, page_size: 100 }),
                ];
                if (isAuthenticated) {
                    requests.push(enrollmentService.getEnrollments({ course: id, page_size: 20 }));
                }

                const [courseRes, subjectsRes, enrollmentsRes] = await Promise.all(requests);

                if (cancelled) return;

                setCourse(courseRes.data);

                const sortedSubjects = parseList(subjectsRes).sort((a, b) => a.order - b.order);

                const subjectsWithLessons = await Promise.all(
                    sortedSubjects.map(async (subj) => {
                        const lessonsRes = await courseService.getLessons({ subject: subj.id, page_size: 100 });
                        const lessons = parseList(lessonsRes).sort((a, b) => a.order - b.order);
                        return { ...subj, lessons };
                    })
                );

                if (cancelled) return;

                setSubjects(subjectsWithLessons);

                if (subjectsWithLessons.length > 0) {
                    setExpandedSubjects({ [subjectsWithLessons[0].id]: true });
                }

                if (enrollmentsRes) {
                    const enrollmentsList = parseList(enrollmentsRes);
                    const match = findCourseEnrollment(enrollmentsList);
                    setEnrollment(match);

                    if (canAccessCourseContent(match)) {
                        const first = findFirstLesson(subjectsWithLessons);
                        if (first) setActiveLesson(first);
                    }
                } else {
                    setEnrollment(null);
                }
            } catch (err) {
                console.error('Error fetching course details:', err);
                if (!cancelled) {
                    toast.error('Failed to load course details. Please try again.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchCourseData();
        return () => { cancelled = true; };
    }, [id, isAuthenticated, findFirstLesson]);

    const handleEnroll = async () => {
        try {
            await enrollmentService.createEnrollment(id);
            toast.success('Enrollment request submitted — you will be notified once approved.');
            const res = await enrollmentService.getEnrollments({ course: id });
            const list = parseList(res);
            setEnrollment(findCourseEnrollment(list));
        } catch {
            toast.error('Failed to submit enrollment request.');
        }
    };

    const toggleSubject = (subjectId) => {
        setExpandedSubjects((prev) => ({ ...prev, [subjectId]: !prev[subjectId] }));
    };

    const handleResume = () => {
        const first = findFirstLesson(subjects);
        if (first) setActiveLesson(first);
        else toast('No lessons available yet for this course.', { icon: '📚' });
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="course-learner-loading">
                    <div className="course-learner-loading-spinner" aria-hidden="true" />
                    <p>Loading your course workspace…</p>
                </div>
            </DashboardLayout>
        );
    }

    if (!course) {
        return (
            <DashboardLayout>
                <div className="course-learner-empty">
                    <BookOpen size={48} />
                    <h2>Course not found</h2>
                    <p>This course may have been removed or you may not have access.</p>
                    <Link to="/courses" className="btn btn-primary">Browse courses</Link>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="course-learner-page">
                {/* Hero */}
                <section className="course-learner-hero">
                    <div className="course-learner-hero-bg" aria-hidden="true">
                        <div className="course-learner-orb course-learner-orb-a" />
                        <div className="course-learner-orb course-learner-orb-b" />
                    </div>

                    <div className="course-learner-hero-inner">
                        <Link to={isEnrolled ? '/dashboard' : '/courses'} className="course-learner-back">
                            <ArrowLeft size={18} />
                            {isEnrolled ? 'Back to dashboard' : 'Back to courses'}
                        </Link>

                        <div className="course-learner-hero-grid">
                            <div className="course-learner-hero-main">
                                {isCompleted && (
                                    <span className="course-learner-badge course-learner-badge-enrolled">
                                        <Award size={14} /> Completed
                                    </span>
                                )}
                                {isEnrolled && !isCompleted && (
                                    <span className="course-learner-badge course-learner-badge-enrolled">
                                        <CheckCircle size={14} /> Enrolled
                                    </span>
                                )}
                                {isPendingApproval && (
                                    <span className="course-learner-badge course-learner-badge-pending">
                                        <Clock3 size={14} /> Pending approval
                                    </span>
                                )}

                                <h1>{course.title}</h1>
                                <p className="course-learner-desc">{course.description}</p>

                                <div className="course-learner-meta">
                                    <span className="course-learner-chip">{course.level || 'All levels'}</span>
                                    <span className="course-learner-chip">
                                        <Layers size={14} /> {course.total_subjects ?? subjects.length} subjects
                                    </span>
                                    <span className="course-learner-chip">
                                        <Timer size={14} /> {lessonStats.totalMinutes || course.duration || 0} min
                                    </span>
                                    <span className="course-learner-chip">
                                        <GraduationCap size={14} /> {course.instructor_name || 'Learnly Expert'}
                                    </span>
                                </div>
                            </div>

                            <aside className="course-learner-panel">
                                {isEnrolled ? (
                                    <>
                                        <div className="course-learner-panel-kicker">
                                            <Sparkles size={14} /> Your progress
                                        </div>
                                        <div className="course-learner-progress-ring-wrap">
                                            <svg viewBox="0 0 120 120" className="course-learner-progress-ring">
                                                <circle cx="60" cy="60" r="52" className="course-learner-ring-bg" />
                                                <circle
                                                    cx="60"
                                                    cy="60"
                                                    r="52"
                                                    className="course-learner-ring-fill"
                                                    style={{
                                                        strokeDasharray: `${2 * Math.PI * 52}`,
                                                        strokeDashoffset: `${2 * Math.PI * 52 * (1 - progressPct / 100)}`,
                                                    }}
                                                />
                                            </svg>
                                            <div className="course-learner-progress-value">
                                                <strong>{progressPct}%</strong>
                                                <span>complete</span>
                                            </div>
                                        </div>
                                        <p className="course-learner-panel-note">
                                            {isCompleted
                                                ? 'You finished this course — review any lesson or assessment anytime.'
                                                : progressPct >= 100
                                                    ? 'Almost there — finish any remaining required assessments if shown on your enrollment page.'
                                                    : `${lessonStats.count} lessons · pick up where you left off`}
                                        </p>
                                        {isCompleted && enrollment?.id && (
                                            <Link
                                                to={`/enrollments/${enrollment.id}`}
                                                className="btn btn-secondary course-learner-cta"
                                            >
                                                <Award size={18} /> View certificate
                                            </Link>
                                        )}
                                        <button type="button" className="btn btn-primary course-learner-cta" onClick={handleResume}>
                                            <PlayCircle size={18} /> {isCompleted ? 'Review course' : 'Continue learning'}
                                        </button>
                                    </>
                                ) : isPendingApproval ? (
                                    <>
                                        <div className="course-learner-panel-icon course-learner-panel-icon-pending">
                                            <Clock3 size={28} />
                                        </div>
                                        <h3>Application under review</h3>
                                        <p className="course-learner-panel-note">
                                            An admin will approve your enrollment shortly. You will receive access to all lessons once approved.
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <div className="course-learner-price">
                                            {Number(course.course_price) > 0
                                                ? `R ${Number(course.course_price).toFixed(2)}`
                                                : 'Free'}
                                        </div>
                                        <p className="course-learner-panel-note">
                                            Join {course.total_students ?? 0}+ learners on this track
                                        </p>
                                        <button type="button" className="btn btn-primary course-learner-cta" onClick={handleEnroll}>
                                            Enroll now
                                        </button>
                                        <p className="course-learner-panel-fineprint">
                                            Instant access on free courses after approval
                                        </p>
                                    </>
                                )}
                            </aside>
                        </div>
                    </div>
                </section>

                {/* Workspace */}
                <div className="course-learner-workspace">
                    <div className="course-learner-workspace-grid">
                        {/* Curriculum */}
                        <aside className="course-learner-curriculum">
                            <div className="course-learner-curriculum-header">
                                <h2>Course content</h2>
                                <span>{lessonStats.count} lessons</span>
                            </div>

                            {subjects.length === 0 ? (
                                <div className="course-learner-curriculum-empty">
                                    <BookOpen size={32} />
                                    <p>Curriculum is being prepared. Check back soon.</p>
                                </div>
                            ) : (
                                <div className="course-learner-curriculum-list">
                                    {subjects.map((subject, idx) => (
                                        <div key={subject.id} className="course-learner-module">
                                            <button
                                                type="button"
                                                className="course-learner-module-toggle"
                                                onClick={() => toggleSubject(subject.id)}
                                                aria-expanded={!!expandedSubjects[subject.id]}
                                            >
                                                <span className="course-learner-module-index">
                                                    {(idx + 1).toString().padStart(2, '0')}
                                                </span>
                                                <span className="course-learner-module-title">{subject.title}</span>
                                                <span className="course-learner-module-count">
                                                    {subject.lessons?.length || 0}
                                                </span>
                                                {expandedSubjects[subject.id]
                                                    ? <ChevronDown size={18} />
                                                    : <ChevronRight size={18} />}
                                            </button>

                                            {expandedSubjects[subject.id] && (
                                                <ul className="course-learner-lessons">
                                                    {(subject.lessons || []).map((lesson) => {
                                                        const isActive = activeLesson?.id === lesson.id;
                                                        const locked = !canAccessLessons;
                                                        return (
                                                            <li key={lesson.id}>
                                                                <button
                                                                    type="button"
                                                                    className={`course-learner-lesson${isActive ? ' is-active' : ''}${locked ? ' is-locked' : ''}`}
                                                                    disabled={locked}
                                                                    onClick={() => setActiveLesson(lesson)}
                                                                >
                                                                    {locked
                                                                        ? <Lock size={15} />
                                                                        : <PlayCircle size={15} />}
                                                                    <span>{lesson.title}</span>
                                                                    <span className="course-learner-lesson-duration">
                                                                        {lesson.duration_minutes || 0}m
                                                                    </span>
                                                                </button>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </aside>

                        {/* Player */}
                        <main className="course-learner-player-area">
                            {activeLesson && canAccessLessons ? (
                                <LessonPlayer
                                    lesson={activeLesson}
                                    enrollment={enrollment}
                                    onComplete={() => toast.success('Lesson marked as complete!')}
                                />
                            ) : (
                                <div className="course-learner-player-placeholder">
                                    <div className="course-learner-player-placeholder-icon">
                                        {canAccessLessons ? <BookOpen size={40} /> : <Lock size={40} />}
                                    </div>
                                    <h2>
                                        {canAccessLessons
                                            ? 'Select a lesson to begin'
                                            : isPendingApproval
                                                ? 'Waiting for enrollment approval'
                                                : 'Enroll to unlock lessons'}
                                    </h2>
                                    <p>
                                        {canAccessLessons
                                            ? 'Choose any lesson from the curriculum panel to start learning.'
                                            : isPendingApproval
                                                ? 'Your request is with the admin team. You will get full access once approved.'
                                                : 'Enroll in this course to access video lessons, readings, and assessments.'}
                                    </p>
                                    {canAccessLessons && (
                                        <button type="button" className="btn btn-primary" onClick={handleResume}>
                                            <PlayCircle size={18} /> Start first lesson
                                        </button>
                                    )}
                                    {!isEnrolled && !isPendingApproval && (
                                        <button type="button" className="btn btn-primary" onClick={handleEnroll}>
                                            Enroll now
                                        </button>
                                    )}
                                </div>
                            )}
                        </main>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
}

export default CourseDetail;
